// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useRef, useEffect, useMemo } from "react";
import { EvidenceBubble } from "./EvidenceBubble";
import "./EvidenceDrawer/EvidenceDrawer.css";
import { useEvidenceStore } from "../../../store/evidenceStore";
import { useVaccineDashboardStore } from "../../../store/vaccineDashboardStore";
import { useAlerts } from "../../../hooks/useAlerts";
import type { EvidenceItem, AbstractTriageBrief } from "../lib/models";
import { aiAbstractTriage, normalizePaperForTriage } from "../../../services/aiEvidenceService";
import { pubmedDetails } from "../../../services/pubmedService";

// Components
import { TabHeader } from "./EvidenceDrawer/components/TabHeader";
import { AISummaryPanel } from "./EvidenceDrawer/components/AISummaryPanel";
import { DetailsView } from "./EvidenceDrawer/components/DetailsView";
import { SearchTab } from "./EvidenceDrawer/components/SearchTab";
import { LibraryTab } from "./EvidenceDrawer/components/LibraryTab";
import { ScenarioTab } from "./EvidenceDrawer/components/ScenarioTab";


// Hooks
import { useDebouncedSearch } from "./EvidenceDrawer/hooks/useDebouncedSearch";
import { usePaperDetails } from "./EvidenceDrawer/hooks/usePaperDetails";
import { useDrawerState } from "./EvidenceDrawer/hooks/useDrawerState";
import { useAnalysisProgress } from "./EvidenceDrawer/hooks/useAnalysisProgress";
import { useNavigationStatePreservation } from "./EvidenceDrawer/hooks/useNavigationStatePreservation";

// Utils
import { deepEqual } from "./EvidenceDrawer/utils/deepEqual";

export const EvidenceDrawer: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"search" | "library" | "scenario">("search");
  const [expandedScenarios, setExpandedScenarios] = useState<Set<string>>(new Set());
  const [isMaximized, setIsMaximized] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // AI Triage State for unsaved papers (search results)
  const [triageBriefByPmid, setTriageBriefByPmid] = useState<Record<string, AbstractTriageBrief>>({});
  const [triageErrorByPmid, setTriageErrorByPmid] = useState<Record<string, { code: string; message: string }>>({});
  const [triageStatus, setTriageStatus] = useState<'idle' | 'loading' | 'cancelled' | 'done' | 'error'>('idle');
  const [failedPmids, setFailedPmids] = useState<string[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Selected input source for filtering
  const [selectedInputSource, setSelectedInputSource] = useState<string | null>(null);

  // NEW: Paper selection state
  const [selectedPmids, setSelectedPmids] = useState<string[]>([]);



  // Store hooks
  const { 
    getCachedDetails, 
    cacheDetails, 
    addEvidence, 
    getEvidenceItems, 
    removeEvidence,
    setAiBrief 
  } = useEvidenceStore();
  const scenarios = useVaccineDashboardStore((state) => state.scenarios);
  const attachEvidenceToScenario = useVaccineDashboardStore((state) => state.attachEvidenceToScenario);
  const detachEvidenceFromScenario = useVaccineDashboardStore((state) => state.detachEvidenceFromScenario);
  const inputs = useVaccineDashboardStore((state) => state.inputs);
  const { showSuccess, showError } = useAlerts();

  const libraryItems = getEvidenceItems();

  // Custom hooks
  const { searchQuery, searchResults, isSearching, searchError, setSearchQuery } = useDebouncedSearch();
  const { progress, startProgress, updatePaperStatus, completeProgress, resetProgress, updateTimeEstimation } = useAnalysisProgress();
  const { preserveState, restoreState } = useNavigationStatePreservation();
  
  const libraryBriefs = useMemo(() => {
    const briefs: Record<string, AbstractTriageBrief> = {};
    libraryItems.forEach(item => {
      if (item.aiBrief) {
        briefs[item.id] = item.aiBrief;
      }
    });
    return briefs;
  }, [libraryItems]);

  const activeBriefs = activeTab === 'search' ? triageBriefByPmid : (activeTab === 'library' ? libraryBriefs : {});
  const activePapers = activeTab === 'search' ? searchResults : (activeTab === 'library' ? libraryItems.map(item => ({ pmid: item.id, year: item.year })) : []);
  const relevantCount = Object.values(triageBriefByPmid).filter(brief => {
    const potentialSources = brief.potentialInputSources?.filter(source => source !== 'none') || [];
    const retrievableInputs = brief.retrievable_inputs?.length || 0;
    return potentialSources.length > 0 || retrievableInputs > 0;
  }).length;
  const analyzedCount = Object.keys(triageBriefByPmid).length;
  const errorCount = Object.keys(triageErrorByPmid).filter(pmid => !triageBriefByPmid[pmid]).length;
  const completionSummary = triageStatus === 'done' && (analyzedCount > 0 || errorCount > 0)
    ? (
      <div
        className={`evidence-drawer__triage-summary ${errorCount > 0 ? 'evidence-drawer__triage-summary--partial' : 'evidence-drawer__triage-summary--success'}`}
      >
        ✓ {analyzedCount} analyzed
        {relevantCount > 0 && ` • ${relevantCount} relevant`}
        {errorCount > 0 && ` • ⚠ ${errorCount} failed`}
      </div>
    )
    : null;
  const noRelevantMessageFragments = [
    'does not provide specific data relevant to vaccine model parameters',
    'does not provide relevant data for hta model parameters'
  ];
  const NO_RELEVANT_DATA_CODE = 'NO_RELEVANT_DATA';

  const isNoRelevantError = (error?: { code?: string; message?: string }) => {
    if (!error) return false;
    if (error.code === NO_RELEVANT_DATA_CODE) return true;
    const message = error.message?.toLowerCase();
    return !!message && noRelevantMessageFragments.some(fragment => message.includes(fragment));
  };

  const buildEmptyBrief = (pmid: string): AbstractTriageBrief => ({
    mode: 'hta_brief',
    abstractHash: `no-relevant-${pmid}`,
    summary: '',
    studyType: 'unknown',
    potentialInputSources: ['none'],
    confidence: 'low',
    cautions: [],
    generatedAt: new Date().toISOString(),
    pmid,
    retrievable_inputs: []
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // NEW: Dynamic time estimation updates during long-running analysis
  useEffect(() => {
    if (triageStatus === 'loading' && progress) {
      const interval = setInterval(() => {
        updateTimeEstimation();
      }, 5000); // Update every 5 seconds

      return () => clearInterval(interval);
    }
  }, [triageStatus, progress, updateTimeEstimation]);

  // Clear triage state when searching starts
  useEffect(() => {
    if (isSearching) {
      setTriageStatus('idle');
      setFailedPmids([]);
      setTriageBriefByPmid({});
      setTriageErrorByPmid({});
      setSelectedInputSource(null);
      setSelectedPmids([]); // Clear selection when new search starts
      resetProgress(); // Clear progress when new search starts
      abortControllerRef.current?.abort();
    }
  }, [isSearching, resetProgress]);

  const { selectedPmid, details, isLoadingDetails, error: paperDetailsError, loadDetails } = usePaperDetails({
    getCachedDetails,
    cacheDetails,
  });
  const { isOpen, isClosing, toggle, close, open } = useDrawerState({
    triggerRef,
    onClose: () => {},
  });

  // NEW: Restore navigation state on mount
  useEffect(() => {
    const savedState = restoreState();
    if (savedState) {
      // Restore analysis state
      setTriageBriefByPmid(savedState.triageBriefs);
      setTriageErrorByPmid(savedState.triageErrors);
      setTriageStatus(savedState.triageStatus);
      setFailedPmids(savedState.failedPmids);
      setSelectedPmids(savedState.selectedPmids);
      
      // Restore UI state
      setActiveTab(savedState.activeTab);
      setSelectedInputSource(savedState.selectedInputSource);
      setSearchQuery(savedState.searchQuery);
      setIsMaximized(savedState.isMaximized ?? false);

      if (savedState.isOpen) {
        open();
      }

      if (savedState.selectedPmid) {
        loadDetails(savedState.selectedPmid);
      }
      
      // Note: analysisProgress is handled by the useAnalysisProgress hook
      // and doesn't need to be restored here as it's session-specific
    }
  }, [loadDetails, open, restoreState, setSearchQuery]);

  // NEW: Preserve state on changes
  useEffect(() => {
    if (
      triageStatus !== 'idle' ||
      selectedPmids.length > 0 ||
      Object.keys(triageBriefByPmid).length > 0 ||
      isOpen ||
      !!selectedPmid ||
      searchQuery.length > 0
    ) {
      preserveState({
        triageBriefs: triageBriefByPmid,
        triageErrors: triageErrorByPmid,
        triageStatus,
        failedPmids,
        selectedPmids,
        analysisProgress: progress,
        selectedPmid,
        activeTab,
        selectedInputSource,
        searchQuery,
        isOpen,
        isMaximized,
      });
    }
  }, [
    triageBriefByPmid, 
    triageErrorByPmid, 
    triageStatus, 
    failedPmids, 
    selectedPmids, 
    progress, 
    selectedPmid,
    activeTab, 
    selectedInputSource, 
    searchQuery,
    isOpen,
    isMaximized,
    preserveState
  ]);

  // Handlers
  const handlePaperClick = (pmid: string, _cachedDetails?: any) => {
    loadDetails(pmid);
  };

  const handleBatchTriage = async (pmidsToTriage?: string[]) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    let candidates: any[] = [];
    if (pmidsToTriage) {
      // Find papers in either search results or library
      const allPossiblePapers = [
        ...searchResults, 
        ...libraryItems.map(i => ({ pmid: i.id, title: i.title, year: i.year, journal: i.journal }))
      ];
      candidates = allPossiblePapers.filter(p => pmidsToTriage.includes(p.pmid));
    } else {
      candidates = activeTab === 'search' 
        ? searchResults.slice(0, 10) 
        : libraryItems.map(i => ({ pmid: i.id, title: i.title, year: i.year, journal: i.journal }));
    }

    if (candidates.length === 0) return;

    setTriageStatus('loading');
    
    // Initialize progress tracking
    const candidatePmids = candidates.map(c => c.pmid);
    startProgress(candidates.length, candidatePmids);

    try {
      // 1. Fetch abstracts for candidates
      const enrichedCandidates = await Promise.all(
        candidates.map(async (p) => {
          try {
            // Update status to analyzing for this paper
            updatePaperStatus(p.pmid, 'analyzing');
            
            // Check cache first
            const cached = getCachedDetails(p.pmid);
            if (cached?.abstract) {
              return { ...p, abstract: cached.abstract };
            }

            const detailsResponse = await pubmedDetails({ pmid: p.pmid }, signal);
            const details = detailsResponse.data;
            if (details) {
              cacheDetails(p.pmid, details);
            }
            return {
              ...p,
              abstract: details.abstract
            };
          } catch (e) {
            console.warn(`Failed to fetch details for ${p.pmid}:`, e);
            updatePaperStatus(p.pmid, 'failed');
            return p;
          }
        })
      );

      const errorMap: Record<string, { code: string; message: string }> = {};
      const failed: string[] = [];
      const missingAbstracts = enrichedCandidates.filter(p => !(p as any).abstract);
      missingAbstracts.forEach(p => {
        errorMap[p.pmid] = { code: 'MISSING_ABSTRACT', message: 'Missing abstract; cannot run AI triage.' };
        failed.push(p.pmid);
        updatePaperStatus(p.pmid, 'failed');
      });

      const itemsToTriage = enrichedCandidates.filter(p => !!(p as any).abstract);

      if (itemsToTriage.length === 0) {
        setTriageStatus('done');
        if (failed.length > 0) {
          setTriageErrorByPmid(prev => ({ ...prev, ...errorMap }));
          setFailedPmids(failed);
        }
        completeProgress();
        return;
      }

      // Update status for papers that will be triaged
      itemsToTriage.forEach(p => {
        updatePaperStatus(p.pmid, 'analyzing');
      });

      const response = await aiAbstractTriage({
        mode: 'hta_brief',
        items: itemsToTriage.map(p => normalizePaperForTriage(p as any))
      }, signal);

      if (signal.aborted) return;

      const successMap: Record<string, AbstractTriageBrief> = {};
      response.data.items.forEach(item => {
        successMap[item.pmid] = item.brief;
        updatePaperStatus(item.pmid, 'completed');
      });

      if (response.errors) {
        response.errors.forEach(err => {
          // Check if this is actually a "no relevant data" case, not a real error
          if (isNoRelevantError(err)) {
            successMap[err.pmid] = buildEmptyBrief(err.pmid);
            updatePaperStatus(err.pmid, 'completed');
            return;
          }
          // Only treat as error if it's a genuine technical failure
          errorMap[err.pmid] = { code: err.code, message: err.message };
          failed.push(err.pmid);
          updatePaperStatus(err.pmid, 'failed');
        });
      }

      // Additionally, check all successful briefs - if they only have 'none' as input source
      // and no retrievable inputs, they should still be treated as successful empty results
      Object.entries(successMap).forEach(([pmid, brief]) => {
        const hasOnlyNone = brief.potentialInputSources?.length === 1 &&
                           brief.potentialInputSources[0] === 'none';
        const hasNoRetrievable = !brief.retrievable_inputs || brief.retrievable_inputs.length === 0;

        // This is a valid "no relevant data" result, ensure it's not in error map
        if (hasOnlyNone && hasNoRetrievable) {
          delete errorMap[pmid];
          const failedIndex = failed.indexOf(pmid);
          if (failedIndex > -1) {
            failed.splice(failedIndex, 1);
          }
        }
      });

      // If we are in library tab, update the store persistently
      if (activeTab === 'library') {
        Object.entries(successMap).forEach(([pmid, brief]) => {
          setAiBrief(pmid, brief);
        });
      } else {
        // Otherwise update local state for search results
        setTriageBriefByPmid(prev => ({ ...prev, ...successMap }));
      }
      
      setTriageErrorByPmid(prev => ({ ...prev, ...errorMap }));
      setFailedPmids(failed);
      setTriageStatus('done');
      completeProgress();

    } catch (error: any) {
      if (signal.aborted) {
        setTriageStatus('cancelled');
        resetProgress();
        return;
      }
      console.error('Batch triage failed:', error);
      setTriageStatus('error');
      resetProgress();
    }
  };

  const handleRetryFailed = () => {
    handleBatchTriage(failedPmids);
  };

  const handleSingleTriage = async (pmid: string, signal: AbortSignal) => {
    const paper = details;
    if (!paper || !paper.abstract) return;

    try {
      const response = await aiAbstractTriage({
        mode: 'hta_brief',
        items: [normalizePaperForTriage(paper)]
      }, signal);

      if (signal.aborted) return;

      const result = response.data.items[0];
      const error = response.errors?.[0];

      if (result) {
        // If paper is already in library, update store
        const isInLibrary = !!libraryItems.find(item => item.id === pmid);
        if (isInLibrary) {
          setAiBrief(pmid, result.brief);
        } else {
          // Otherwise update local state for search results
          setTriageBriefByPmid(prev => ({ ...prev, [pmid]: result.brief }));
        }
      } else if (error) {
        if (isNoRelevantError(error)) {
          const emptyBrief = buildEmptyBrief(pmid);
          const isInLibrary = !!libraryItems.find(item => item.id === pmid);
          if (isInLibrary) {
            setAiBrief(pmid, emptyBrief);
          } else {
            setTriageBriefByPmid(prev => ({ ...prev, [pmid]: emptyBrief }));
          }
          return;
        }
        setTriageErrorByPmid(prev => ({ ...prev, [pmid]: error }));
        throw new Error(error.message);
      }
    } catch (err: any) {
      if (signal.aborted) return;
      throw err;
    }
  };

  const handleSaveToLibrary = () => {
    if (!details) return;

    try {
      // Check if we have a local triage brief for this paper
      const localBrief = triageBriefByPmid[details.pmid];

      // Convert PubmedPaperDetails to EvidenceItem (metadata only)
      const evidenceItem: EvidenceItem = {
        id: details.pmid,
        source: "pubmed",
        title: details.title,
        // abstract: NOT saved (storage optimization)
        journal: details.journal,
        year: details.year,
        firstAuthor: details.authors?.[0],
        doi: details.doi,
        url: details.url,
        addedAt: new Date().toISOString(),
        aiBrief: localBrief, // Include triage brief if we have it
      };
      addEvidence(evidenceItem);
      cacheDetails(details.pmid, details);
      showSuccess('Paper saved to library');
    } catch (error) {
      // Handle specific error types
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        showError('Storage limit reached. Please remove some saved papers.');
      } else if (error instanceof Error) {
        showError(`Failed to save paper: ${error.message}`);
      } else {
        showError('Failed to save paper to library');
      }
    }
  };

  const handleAttachToScenario = () => {
    if (!details) return;

    try {
      // Find scenario matching current inputs using proper deep equality
      const matchingScenario = scenarios.find((s) => 
        s.inputs && inputs && deepEqual(s.inputs, inputs)
      );
      
      if (matchingScenario) {
        attachEvidenceToScenario(matchingScenario.id, details.pmid);
        showSuccess(`Paper attached to scenario: ${matchingScenario.name}`);
      } else if (scenarios.length > 0) {
        // If no exact match, attach to most recent scenario (last in array)
        const mostRecentScenario = scenarios[scenarios.length - 1];
        attachEvidenceToScenario(mostRecentScenario.id, details.pmid);
        showSuccess(`Paper attached to scenario: ${mostRecentScenario.name}`);
      } else {
        showError('No scenarios available. Please create a scenario first.');
      }
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to attach paper to scenario');
    }
  };

  const handleRemoveFromLibrary = (pmid: string) => {
    removeEvidence(pmid);
    showSuccess('Paper removed from library');
  };

  const handleDetachFromScenario = (scenarioId: string, pmid: string) => {
    detachEvidenceFromScenario(scenarioId, pmid);
    showSuccess('Paper detached from scenario');
  };

  const handleTabChange = (tab: "search" | "library" | "scenario") => {
    setActiveTab(tab);
    setSelectedInputSource(null);
  };

  // NEW: Selection handlers
  const handlePaperSelectionChange = (pmid: string, selected: boolean) => {
    setSelectedPmids(prev => 
      selected 
        ? [...prev, pmid]
        : prev.filter(id => id !== pmid)
    );
  };

  const handleSelectAll = () => {
    const availablePmids = searchResults.map(paper => paper.pmid);
    setSelectedPmids(availablePmids);
  };

  const handleSelectNone = () => {
    setSelectedPmids([]);
  };

  return (
    <>
      <EvidenceBubble
        ref={triggerRef}
        onClick={toggle}
        isOpen={isOpen}
        aria-expanded={isOpen && !isClosing}
        aria-label={isOpen ? "Close evidence drawer" : "Open evidence drawer"}
      />
      {(isOpen || isClosing) && (
        <>
          <div
            className={`evidence-drawer__overlay ${isClosing ? "evidence-drawer__overlay--closing" : ""}`}
            onClick={close}
            aria-hidden="true"
          />
          <div
            className={`evidence-drawer__panel ${isClosing ? "evidence-drawer__panel--closing" : ""} ${isMaximized ? "evidence-drawer__panel--maximized" : ""}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="evidence-drawer-title"
            aria-modal="true"
          >
            <div className="evidence-drawer__header">
              <h2 className="evidence-drawer__title" id="evidence-drawer-title">
                Evidence Library
              </h2>
              <div className="evidence-drawer__header-actions">
                <button
                  className="evidence-drawer__maximize"
                  onClick={() => setIsMaximized(!isMaximized)}
                  aria-label={isMaximized ? "Restore evidence drawer size" : "Maximize evidence drawer"}
                >
                  {isMaximized ? "❐" : "▢"}
                </button>
                <button
                  className="evidence-drawer__close"
                  onClick={close}
                  aria-label="Close evidence drawer"
                >
                  ×
                </button>
              </div>
            </div>

            {selectedPmid ? (
              <DetailsView 
                paper={details} 
                onBack={() => loadDetails(null)} 
                isLoading={isLoadingDetails}
                error={paperDetailsError}
                onSaveToLibrary={details ? handleSaveToLibrary : undefined}
                onAttachToScenario={details ? handleAttachToScenario : undefined}
                aiBrief={details ? (libraryItems.find(i => i.id === details.pmid)?.aiBrief || triageBriefByPmid[details.pmid]) : undefined}
                onTriageRequest={handleSingleTriage}
              />
            ) : (
              <>
                {/* CONTROLS REGION: Tabs, Search, Filters, Selection Count, Primary CTA */}
                <div className="evidence-drawer__controls-region">
                  {/* Tabs */}
                  <TabHeader activeTab={activeTab} onTabChange={handleTabChange} />
                  
                  {/* AI Summary Panel (optional, shown when briefs exist) */}
                  {Object.keys(activeBriefs).length > 0 && (
                    <AISummaryPanel 
                      briefs={activeBriefs}
                      papers={activePapers}
                      selectedInputSource={selectedInputSource}
                      onTagClick={(source) => setSelectedInputSource(prev => prev === source ? null : source)}
                    />
                  )}
                </div>

                {/* RESULTS REGION: Tab Content */}
                <div
                  className="evidence-drawer__results-region"
                  role="tabpanel"
                  id={`tabpanel-${activeTab}`}
                  aria-labelledby={`tab-${activeTab}`}
                >
                  {activeTab === "search" && (
                    <SearchTab
                      searchQuery={searchQuery}
                      setSearchQuery={setSearchQuery}
                      isSearching={isSearching}
                      searchError={searchError}
                      searchResults={searchResults}
                      onPaperClick={handlePaperClick}
                      // Pass triage state and actions down
                      triageBriefs={triageBriefByPmid}
                      triageErrors={triageErrorByPmid}
                      triageStatus={triageStatus}
                      failedPmids={failedPmids}
                      onBatchTriage={handleBatchTriage}
                      onRetryFailed={handleRetryFailed}
                      selectedInputSource={selectedInputSource}
                      // NEW: Selection props
                      selectedPmids={selectedPmids}
                      onSelectionChange={handlePaperSelectionChange}
                      onSelectAll={handleSelectAll}
                      onSelectNone={handleSelectNone}
                      // NEW: Progress props
                      analysisProgress={progress}
                      completionSummary={completionSummary}
                    />
                  )}
                  {activeTab === "library" && (
                    <LibraryTab
                      libraryItems={libraryItems}
                      getCachedDetails={getCachedDetails}
                      onPaperClick={handlePaperClick}
                      onRemove={handleRemoveFromLibrary}
                      selectedInputSource={selectedInputSource}
                      // Pass triage state and actions down
                      triageBriefs={libraryBriefs}
                      triageErrors={triageErrorByPmid}
                      triageStatus={triageStatus}
                      failedPmids={failedPmids}
                      onBatchTriage={handleBatchTriage}
                      onRetryFailed={handleRetryFailed}
                    />
                  )}
                  {activeTab === "scenario" && (
                    <ScenarioTab
                      scenarios={scenarios}
                      expandedScenarios={expandedScenarios}
                      setExpandedScenarios={setExpandedScenarios}
                      getCachedDetails={getCachedDetails}
                      libraryItems={libraryItems}
                      onPaperClick={handlePaperClick}
                      onDetach={handleDetachFromScenario}
                      selectedInputSource={selectedInputSource}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};
